import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { calculateArcCoStressTest } from "@datanac/datanac-api-toolkit/dist/arcplc/ArcCoStressTest";
import {
  BENCHMARK_GUARANTEE_PERCENTAGE,
  calculateArcCoPayment,
  calculatePlcPayment,
  PLC_PAYMENT_PERCENTAGE,
} from "@datanac/datanac-api-toolkit/dist/arcplc/ArcPlcHelper";
import { calculatePlcStressTest } from "@datanac/datanac-api-toolkit/dist/arcplc/PlcStressTest";
import {
  PRACTICE_NAME_IRRIGATED,
  PRACTICE_NAME_NON_IRRIGATED,
} from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import { ApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { ArcPlcService, PROGRAM_NAME_ARCCO, PROGRAM_NAME_PLC } from "components/ArcPlc/ArcPlcService";
import { InsuranceService } from "components/Insurance/InsuranceService";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { validate as validateUuid } from "uuid";

export const ERROR_NO_COUNTY_DATA_FOUND = "Could not locate county data.";

function normalizeArcCoTypeNameForMatch(type_name) {
  if (type_name == null || type_name === "") return "";
  if (validateUuid(String(type_name))) return "";
  return String(type_name).toLowerCase();
}

/** Route segment `_` or a type UUID means "no type" for override/API filters. */
export function normalizeArcPlcRouteTypeName(type_name) {
  if (type_name == null || type_name === "" || type_name === "_") return undefined;
  if (validateUuid(String(type_name))) return undefined;
  return type_name;
}

/**
 * Match a base-acre row to a saved ArcCoCountyYieldOverride (users API).
 * Aligns type handling with ArcPlcCountyBaseAcreWorkspace (UUID type_name omitted in API).
 */
export function findArcCoCountyYieldOverrideForBaseAcre(baseAcre, overrides) {
  if (!baseAcre || !overrides?.length) return null;
  const st = (baseAcre.state_name || baseAcre.location_state_name)?.toLowerCase?.();
  const cn = (baseAcre.county_name || baseAcre.location_county_name)?.toLowerCase?.();
  const cm = baseAcre.commodity_name?.toLowerCase?.();
  const cropYear = baseAcre.crop_year;
  const baseType = normalizeArcCoTypeNameForMatch(baseAcre.type_name);
  return (
    overrides.find((o) => {
      if (!o) return false;
      if (String(o.crop_year) !== String(cropYear)) return false;
      if (o.state_name?.toLowerCase?.() !== st) return false;
      if (o.county_name?.toLowerCase?.() !== cn) return false;
      if (o.commodity_name?.toLowerCase?.() !== cm) return false;
      const ot = normalizeArcCoTypeNameForMatch(o.type_name);
      return baseType === ot;
    }) || null
  );
}

function parseOptionalArcOverrideNumber(val) {
  if (val === "" || val == null) return null;
  const n = typeof val === "string" ? parseFloat(String(val).replace(/[^0-9.-]+/g, "")) : Number(val);
  return Number.isFinite(n) ? n : null;
}

/**
 * County workspace keeps live edits in countyYieldOverrideRecord; that record wins over
 * stale rows from ArcCoCountyYieldOverride/search until reload completes.
 */
function resolveArcOverrideMyap(rowOverride, market_year_average_price, countyYieldOverrideRecord) {
  if (countyYieldOverrideRecord != null) {
    return parseOptionalArcOverrideNumber(countyYieldOverrideRecord.market_year_average_price);
  }
  const fromHook = parseOptionalArcOverrideNumber(market_year_average_price);
  if (fromHook != null) return fromHook;
  return rowOverride ? parseOptionalArcOverrideNumber(rowOverride.market_year_average_price) : null;
}

function resolveArcOverrideYield(rowOverride, fieldName, hookYield, countyYieldOverrideRecord) {
  if (countyYieldOverrideRecord != null) {
    return parseOptionalArcOverrideNumber(countyYieldOverrideRecord[fieldName]);
  }
  const fromHook = parseOptionalArcOverrideNumber(hookYield);
  if (fromHook != null) return fromHook;
  return rowOverride ? parseOptionalArcOverrideNumber(rowOverride[fieldName]) : null;
}

/** FSA county yield for display/calc: user override, then FSA actual_yield, then benchmark_yield. */
function resolveArcCoCountyYield(rowOverride, overrideFieldName, hookYield, countyYieldOverrideRecord, fsaYieldRow) {
  return (
    resolveArcOverrideYield(rowOverride, overrideFieldName, hookYield, countyYieldOverrideRecord) ??
    parseOptionalArcOverrideNumber(fsaYieldRow?.actual_yield) ??
    parseOptionalArcOverrideNumber(fsaYieldRow?.benchmark_yield)
  );
}

function fsaArcCoCountyYield(fsaYieldRow) {
  return (
    parseOptionalArcOverrideNumber(fsaYieldRow?.actual_yield) ??
    parseOptionalArcOverrideNumber(fsaYieldRow?.benchmark_yield)
  );
}

import {
  getDefaultHistoricalIrrigationPercentage,
  getShareWeightedHistoricalIrrigationPercentage,
  HISTORICAL_IRRIGATION_PERCENTAGE_DEFAULT,
  usesDefaultHistoricalIrrigationPercentage,
} from "./historicalIrrigationPercentage";

export {
  getDefaultHistoricalIrrigationPercentage,
  getShareWeightedHistoricalIrrigationPercentage,
  HISTORICAL_IRRIGATION_PERCENTAGE_DEFAULT,
  usesDefaultHistoricalIrrigationPercentage,
};

// Utility function for calculating weighted values - can be used in other components
export const calculateWeightedValue = (
  rowIndex,
  columnIndex,
  fieldName,
  insurancePlanUuid,
  stressTestResults,
  stressTestResultsIrrigated,
  stressTestResultsNonIrrigated
) => {
  if (rowIndex === undefined || columnIndex === undefined || !stressTestResults) {
    return null;
  }

  const irrigatedCell = stressTestResultsIrrigated?.length && stressTestResultsIrrigated[rowIndex]?.[columnIndex];
  const nonIrrigatedCell =
    stressTestResultsNonIrrigated?.length && stressTestResultsNonIrrigated[rowIndex]?.[columnIndex];

  if (!irrigatedCell || !nonIrrigatedCell) {
    return stressTestResults[rowIndex]?.[columnIndex]?.indemnityResults[0][fieldName] || 0;
  }

  const irrigatedBaseAcreage = irrigatedCell?.base_acreage || 0;
  const nonIrrigatedBaseAcreage = nonIrrigatedCell?.base_acreage || 0;
  const totalAcreage = irrigatedBaseAcreage + nonIrrigatedBaseAcreage;

  if (totalAcreage === 0) {
    return 0;
  }

  let irrigatedValue = 0;
  let nonIrrigatedValue = 0;

  if (insurancePlanUuid) {
    const irrigatedPlan = irrigatedCell?.indemnityResults?.find((r) => r.insurance_plan_uuid === insurancePlanUuid);
    const nonIrrigatedPlan = nonIrrigatedCell?.indemnityResults?.find(
      (r) => r.insurance_plan_uuid === insurancePlanUuid
    );

    irrigatedValue = irrigatedPlan?.[fieldName] || 0;
    nonIrrigatedValue = nonIrrigatedPlan?.[fieldName] || 0;
  } else {
    irrigatedValue = irrigatedCell?.indemnityResults?.[0]?.[fieldName] || 0;
    nonIrrigatedValue = nonIrrigatedCell?.indemnityResults?.[0]?.[fieldName] || 0;
  }

  const weightedValue =
    (irrigatedValue * irrigatedBaseAcreage + nonIrrigatedValue * nonIrrigatedBaseAcreage) / totalAcreage;

  return weightedValue;
};

// Function to create weighted stress test results from irrigated and non-irrigated results
export const createWeightedStressTestResults = (stressTestResultsIrrigated, stressTestResultsNonIrrigated) => {
  // Handle case where one or both inputs are missing
  if (!stressTestResultsIrrigated?.results || !stressTestResultsNonIrrigated?.results) {
    return stressTestResultsIrrigated || stressTestResultsNonIrrigated || null;
  }

  // Extract the results arrays from the stress test objects
  const irrigatedResults = stressTestResultsIrrigated.results;
  const nonIrrigatedResults = stressTestResultsNonIrrigated.results;

  if (!irrigatedResults?.length || !nonIrrigatedResults?.length) {
    return stressTestResultsIrrigated || stressTestResultsNonIrrigated || null;
  }

  // Create a deep copy of the entire irrigated stress test object as our base structure
  const weightedStressTestResults = JSON.parse(JSON.stringify(stressTestResultsIrrigated));
  const weightedResults = weightedStressTestResults.results;

  // Iterate through each row and column to create weighted values
  for (let rowIndex = 0; rowIndex < weightedResults.length; rowIndex++) {
    for (let columnIndex = 0; columnIndex < weightedResults[rowIndex].length; columnIndex++) {
      const cell = weightedResults[rowIndex][columnIndex];

      if (cell && cell.indemnityResults) {
        // Calculate weighted values for each indemnity result
        cell.indemnityResults.forEach((indemnityResult) => {
          const insurancePlanUuid = indemnityResult.insurance_plan_uuid;

          // Also calculate weighted values for other important fields
          const fieldsToWeight = ["indemnity_total", "indemnity_total_per_base_acre", "indemnity_total_per_acre"];

          fieldsToWeight.forEach((field) => {
            // Calculate weighted field value using the results arrays
            const weightedFieldValue = calculateWeightedValue(
              rowIndex,
              columnIndex,
              field,
              insurancePlanUuid,
              weightedResults, // Pass the results array, not the full object
              irrigatedResults,
              nonIrrigatedResults
            );

            // Update the indemnity result with the weighted value
            indemnityResult[field] = weightedFieldValue;
          });
        });

        // Also update cell-level properties that might exist
        const fieldsToWeight = ["indemnity_total", "indemnity_total_per_base_acre", "indemnity_total_per_acre"];
        fieldsToWeight.forEach((field) => {
          // For cell-level properties, use the first insurance plan or default
          const weightedFieldValue = calculateWeightedValue(
            rowIndex,
            columnIndex,
            field,
            null, // No specific insurance plan UUID for cell-level properties
            weightedResults,
            irrigatedResults,
            nonIrrigatedResults
          );

          // Update cell-level property if it exists
          if (cell[field] !== undefined) {
            cell[field] = weightedFieldValue;
          }
        });
      }
    }
  }

  // Return the complete stress test object structure with weighted results
  return weightedStressTestResults;
};

export const calculateCombinedArcCoResults = (arcPlc) => {
  return arcPlc.totals.arccoResponsesByPractice?.length > 0
    ? (() => {
        const irrigatedResponse = arcPlc.totals.arccoResponsesByPractice.find(
          (r) => r.practice_name === PRACTICE_NAME_IRRIGATED
        );
        const nonIrrigatedResponse = arcPlc.totals.arccoResponsesByPractice.find(
          (r) => r.practice_name === PRACTICE_NAME_NON_IRRIGATED
        );
        const hip = irrigatedResponse?.historical_irrigation_percentage;

        if (irrigatedResponse && nonIrrigatedResponse) {
          return {
            practice_name: "Combined",
            historical_irrigation_percentage: hip,

            // Weighted combination of all calculation values
            benchmark_price: irrigatedResponse.benchmark_price * hip + nonIrrigatedResponse.benchmark_price * (1 - hip),
            benchmark_yield: irrigatedResponse.benchmark_yield * hip + nonIrrigatedResponse.benchmark_yield * (1 - hip),
            benchmark_revenue:
              irrigatedResponse.benchmark_revenue * hip + nonIrrigatedResponse.benchmark_revenue * (1 - hip),
            benchmark_guarantee:
              irrigatedResponse.benchmark_guarantee * hip + nonIrrigatedResponse.benchmark_guarantee * (1 - hip),
            actual_revenue: irrigatedResponse.actual_revenue * hip + nonIrrigatedResponse.actual_revenue * (1 - hip),

            // Simple addition of individual practice payments
            payment: (irrigatedResponse.payment || 0) + (nonIrrigatedResponse.payment || 0),
            maxPayment: (irrigatedResponse.maxPayment || 0) + (nonIrrigatedResponse.maxPayment || 0),
            totalFarmPayment: (irrigatedResponse.totalFarmPayment || 0) + (nonIrrigatedResponse.totalFarmPayment || 0),
            arcPayment: (irrigatedResponse.arcPayment || 0) * hip + (nonIrrigatedResponse.arcPayment || 0) * (1 - hip),

            // Use values from irrigated response as base for other properties
            base_acreage: arcPlc.totals.base_acreage,
            share_percent: arcPlc.totals.share_percent,
            isPaymentCapped: irrigatedResponse.isPaymentCapped || nonIrrigatedResponse.isPaymentCapped,

            // Constants (same for both practices)
            BENCHMARK_GUARANTEE_PERCENTAGE: BENCHMARK_GUARANTEE_PERCENTAGE,
            MAX_PAYMENT_THRESHOLD: irrigatedResponse.MAX_PAYMENT_THRESHOLD || 0.1,
            BASE_ACRE_PERCENTAGE: irrigatedResponse.BASE_ACRE_PERCENTAGE || 0.85,
            HISTORICAL_IRRIGATION_PERCENTAGE: hip,
          };
        } else {
          // Return a single
          return arcPlc.totals.arccoResponsesByPractice[0];
        }
      })()
    : null;
};

export const useArcPlc = ({
  baseAcres,
  // Stress test dependencies
  budgetCountyPractices = null,
  budgetTotalReportedAcreage = 0,
  historicalIrrigationPercentage = null,
  arcCoDisplayMode = null,
  location_state_name = null,
  location_county_name = null,
  commodity_name = null,
  type_name = null,
  crop_year = null,
  // Stress test parameters
  arcCoStressTestParametersIrrigated = null,
  arcCoStressTestParametersNonIrrigated = null,
  plcStressTestParameters = null,

  // Benchmark yields override
  // Yields override for Irrigated practice
  final_county_yield_irrigated = null,
  // Yields override for Non-irrigated practice or commodities without irrigation
  final_county_yield_non_irrigated = null,

  market_year_average_price = null,

  // Live county/crop override object from ArcPlcCountyBaseAcreWorkspace (edits apply before search reload)
  countyYieldOverrideRecord = null,

  // Rows from ArcCoCountyYieldOverride/search (county / crop / type–specific saved overrides)
  arcCoCountyYieldOverrides = null,

  // API config override (for agent viewing producer data)
  apiConfig = null,
}) => {
  const { globalState: contextGlobalState } = useContext(AppContext);
  // Use provided apiConfig if available, otherwise use context globalState
  const globalState = apiConfig || contextGlobalState;
  const { enqueueSnackbar } = useSnackbar();

  // Content keys: callers often get fresh array references after fetches even when rows are unchanged.
  const baseAcresStableKey = JSON.stringify(baseAcres ?? []);
  const arcOverridesStableKey = JSON.stringify(arcCoCountyYieldOverrides ?? null);
  const countyYieldOverrideStableKey = JSON.stringify(countyYieldOverrideRecord ?? null);

  // Do not depend on `apiConfig` / context object identity — callers often pass `{ ...globalState, producer_token }`
  // each render, which would recreate ArcPlcService every time and destabilize getArcPlcByElection → update loops.
  const arcPlcActions = useMemo(() => {
    const resolvedConfig = apiConfig || contextGlobalState;
    return new ArcPlcService({
      apiConfig: resolvedConfig,
      crop_year: crop_year || (baseAcres?.length ? baseAcres[0]?.crop_year : null),
      baseAcres: baseAcres,
    }).actions;
  }, [
    apiConfig?.producer_token,
    apiConfig?.agent_token,
    contextGlobalState?.producer_token,
    contextGlobalState?.agent_token,
    crop_year,
    baseAcresStableKey,
  ]);

  // --- --- ---

  const [stressTestParametersOutput, setStressTestParametersOutput] = useState(null);

  // --- --- ---
  // Datanac

  const [plcPaymentRates, setPlcPaymentRates] = useState(null);
  const _loadPlcPaymentRates = async () => {
    const _plcPaymentRates = await arcPlcActions.getPLCPaymentRates();
    setPlcPaymentRates(_plcPaymentRates);
  };

  useEffect(() => {
    if (baseAcres?.length) {
      _loadPlcPaymentRates();
    }
  }, [baseAcresStableKey]);

  // ---

  const [marketYearAveragePrices, setMarketYearAveragePrices] = useState(null);
  const _loadMarketYearAveragePrices = async () => {
    const _marketYearAveragePrices = await arcPlcActions.getMarketYearAveragePrices();
    setMarketYearAveragePrices(_marketYearAveragePrices);
  };

  useEffect(() => {
    if (baseAcres?.length) {
      _loadMarketYearAveragePrices();
    }
  }, [baseAcresStableKey]);

  // ---

  const [effectiveReferencePrices, setEffectiveReferencePrices] = useState(null);
  const _loadEffectiveReferencePrices = async () => {
    const _effectiveReferencePrices = await arcPlcActions.getEffectiveReferencePrices();
    setEffectiveReferencePrices(_effectiveReferencePrices);
  };

  useEffect(() => {
    if (baseAcres?.length) {
      _loadEffectiveReferencePrices();
    }
  }, [baseAcresStableKey]);

  // ---

  const [arccoYields, setARCCOYields] = useState(null);
  // Yields for Irrigated practice
  const [benchmarkYieldIrrigated, setBenchmarkYieldIrrigated] = useState(null);
  // Yields for Non-irrigated practice or commodities without irrigation
  const [benchmarkYieldNonIrrigated, setBenchmarkYieldNonIrrigated] = useState(null);

  const _loadARCCOYields = async () => {
    const _ARCCOYields = await arcPlcActions.getARCCOYields();
    setARCCOYields(_ARCCOYields);
  };

  useEffect(() => {
    const irrigatedFsaRow = arccoYields?.find((y) => y?.practice_name === PRACTICE_NAME_IRRIGATED);
    const nonIrrigatedFsaRow = arccoYields?.find((y) => y?.practice_name !== PRACTICE_NAME_IRRIGATED);

    const rowOverride = baseAcres?.[0]
      ? findArcCoCountyYieldOverrideForBaseAcre(baseAcres[0], arcCoCountyYieldOverrides)
      : null;

    setBenchmarkYieldIrrigated(
      resolveArcCoCountyYield(
        rowOverride,
        "final_county_yield_irrigated",
        final_county_yield_irrigated,
        countyYieldOverrideRecord,
        irrigatedFsaRow
      )
    );
    setBenchmarkYieldNonIrrigated(
      resolveArcCoCountyYield(
        rowOverride,
        "final_county_yield_non_irrigated",
        final_county_yield_non_irrigated,
        countyYieldOverrideRecord,
        nonIrrigatedFsaRow
      )
    );
  }, [
    arccoYields,
    baseAcresStableKey,
    arcOverridesStableKey,
    countyYieldOverrideStableKey,
    final_county_yield_irrigated,
    final_county_yield_non_irrigated,
  ]);

  useEffect(() => {
    if (baseAcres?.length) {
      _loadARCCOYields();
    }
  }, [baseAcresStableKey]);

  // --- Program Elections ---
  const [programElections, setProgramElections] = useState(null);
  const [isLoadingProgramElections, setIsLoadingProgramElections] = useState(false);

  // Clear cached FSA reference data when base acres change (e.g. year toggle) so callers
  // do not read prior-year plcPaymentRates / programElections while new fetches run.
  useEffect(() => {
    setPlcPaymentRates(null);
    setMarketYearAveragePrices(null);
    setEffectiveReferencePrices(null);
    setARCCOYields(null);
    setBenchmarkYieldIrrigated(null);
    setBenchmarkYieldNonIrrigated(null);
    setProgramElections(null);
  }, [baseAcresStableKey]);

  const _loadProgramElections = async () => {
    try {
      const crop_year = baseAcres?.length ? baseAcres[0]?.crop_year : null;
      const producer_token = globalState?.producer_token;

      if (!crop_year || !producer_token) return;

      setIsLoadingProgramElections(true);

      const programElectionFilter = {
        crop_year: crop_year,
        producer_token: producer_token,
        program_category: "arc_plc",
        is_active: true,
      };

      const programElectionsData = await UsersApiHelper.users_selectObject(
        "ProgramElection",
        programElectionFilter,
        globalState
      );

      setProgramElections(programElectionsData || []);
    } catch (err) {
      console.warn("Error loading program elections:", err);
      setProgramElections([]);
    } finally {
      setIsLoadingProgramElections(false);
    }
  };

  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      _loadProgramElections();
    }
  }, [baseAcresStableKey, globalState?.producer_token]);

  // --- --- ---

  // New method to calculate ARC/PLC payments based on program elections
  const getArcPlcByElection = useCallback(
    async (externalProgramElections) => {
      // Use external program elections if provided, otherwise use internal ones
      const currentProgramElections = externalProgramElections || programElections;

      if (!baseAcres?.length || !currentProgramElections?.length) {
        console.warn("getArcPlcByElection 0", "** No data loaded", baseAcres?.length, currentProgramElections?.length);
        return {};
      }

      try {
        // Get required data - use cached if available, otherwise load fresh
        const [
          currentPlcPaymentRates,
          currentMarketYearAveragePrices,
          currentEffectiveReferencePrices,
          currentArccoYields,
        ] = await Promise.all([
          plcPaymentRates || arcPlcActions.getPLCPaymentRates(),
          marketYearAveragePrices || arcPlcActions.getMarketYearAveragePrices(),
          effectiveReferencePrices || arcPlcActions.getEffectiveReferencePrices(),
          arccoYields || arcPlcActions.getARCCOYields(),
        ]);

        // Get unique commodities from program elections
        const commodities = [...new Set(currentProgramElections.map((pe) => pe.commodity_name))];
        const paymentPromises = commodities.map(async (commodityName) => {
          // Filter program elections for this commodity
          const relevantProgramElections = currentProgramElections.filter(
            (pe) => pe.commodity_name?.toLowerCase() === commodityName?.toLowerCase()
          );

          if (!relevantProgramElections.length) return { commodityName, payment: 0 };

          // Filter baseAcres records based on program elections
          const filteredBaseAcres = baseAcres.filter((baseAcre) => {
            return relevantProgramElections.some(
              (pe) =>
                pe.commodity_name?.toLowerCase() === baseAcre.commodity_name?.toLowerCase() &&
                pe.state_name?.toLowerCase() === (baseAcre.state_name || baseAcre.location_state_name)?.toLowerCase() &&
                pe.county_name?.toLowerCase() === (baseAcre.county_name || baseAcre.location_county_name)?.toLowerCase()
            );
          });

          if (!filteredBaseAcres.length) return { commodityName, payment: 0 };

          let totalArcPayment = 0;
          let totalPlcPayment = 0;

          // Calculate payments for each filtered baseAcres record
          for (const currentBaseAcres of filteredBaseAcres) {
            // Find the corresponding program election
            const programElection = relevantProgramElections.find(
              (pe) =>
                pe.commodity_name?.toLowerCase() === currentBaseAcres.commodity_name?.toLowerCase() &&
                pe.state_name?.toLowerCase() ===
                  (currentBaseAcres.state_name || currentBaseAcres.location_state_name)?.toLowerCase() &&
                pe.county_name?.toLowerCase() ===
                  (currentBaseAcres.county_name || currentBaseAcres.location_county_name)?.toLowerCase()
            );

            if (!programElection) continue;

            const rowOverride = findArcCoCountyYieldOverrideForBaseAcre(currentBaseAcres, arcCoCountyYieldOverrides);
            const overrideMyap = resolveArcOverrideMyap(
              rowOverride,
              market_year_average_price,
              countyYieldOverrideRecord
            );
            const overrideFinalIrrigated = resolveArcOverrideYield(
              rowOverride,
              "final_county_yield_irrigated",
              final_county_yield_irrigated,
              countyYieldOverrideRecord
            );
            const overrideFinalNonIrrigated = resolveArcOverrideYield(
              rowOverride,
              "final_county_yield_non_irrigated",
              final_county_yield_non_irrigated,
              countyYieldOverrideRecord
            );

            // Get current payment rates and prices (same logic as selectArcPlc)
            const _baseTypeName = currentBaseAcres?.type_name || null;

            const currentPlcPaymentRate = currentPlcPaymentRates?.find(
              (plc) =>
                plc?.commodity_name?.toLowerCase() === currentBaseAcres?.commodity_name?.toLowerCase() &&
                plc?.crop_year == currentBaseAcres?.crop_year &&
                (_baseTypeName ? plc?.type_name?.toLowerCase() === _baseTypeName.toLowerCase() : true)
            );
            const plcPaymentRate = currentPlcPaymentRate?.length ? currentPlcPaymentRate[0] : {};

            const currentMarketYearAveragePrice = currentMarketYearAveragePrices?.find(
              (row) =>
                row?.commodity_name?.toLowerCase() === currentBaseAcres?.commodity_name?.toLowerCase() &&
                row?.crop_year == currentBaseAcres?.crop_year &&
                (_baseTypeName ? row?.type_name?.toLowerCase() === _baseTypeName.toLowerCase() : true)
            )?.MarketYearAveragePrices;
            const marketYearAveragePrice = currentMarketYearAveragePrice?.length
              ? currentMarketYearAveragePrice[0]
              : {};

            const currentEffectiveReferencePrice = currentEffectiveReferencePrices?.find(
              (row) =>
                row?.commodity_name?.toLowerCase() === currentBaseAcres?.commodity_name?.toLowerCase() &&
                row?.crop_year == currentBaseAcres?.crop_year &&
                (_baseTypeName ? row?.type_name?.toLowerCase() === _baseTypeName.toLowerCase() : true)
            );
            const effectiveReferencePrice = currentEffectiveReferencePrice?.EffectiveReferencePrices?.length
              ? currentEffectiveReferencePrice?.EffectiveReferencePrices[0]
              : {};

            // Calculate ARC-CO payment if program election is ARC-CO
            if (programElection.program_election === PROGRAM_NAME_ARCCO) {
              const countyArcCoYields = currentArccoYields?.filter(
                (arcCoYield) =>
                  arcCoYield?.state_name?.toLowerCase() ===
                    (currentBaseAcres?.state_name || currentBaseAcres?.location_state_name)?.toLowerCase() &&
                  arcCoYield?.county_name?.toLowerCase() ===
                    (currentBaseAcres?.county_name || currentBaseAcres?.location_county_name)?.toLowerCase() &&
                  arcCoYield?.commodity_name?.toLowerCase() === currentBaseAcres?.commodity_name?.toLowerCase() &&
                  arcCoYield?.crop_year == currentBaseAcres?.crop_year &&
                  (_baseTypeName ? arcCoYield?.type_name?.toLowerCase() === _baseTypeName.toLowerCase() : true)
              );

              let arcPaymentTotal = 0;

              countyArcCoYields?.forEach((currentArcCoYield) => {
                const fsaCountyYield = fsaArcCoCountyYield(currentArcCoYield);
                const finalCountyYield =
                  currentArcCoYield.practice_name === PRACTICE_NAME_IRRIGATED
                    ? (overrideFinalIrrigated ?? fsaCountyYield)
                    : currentArcCoYield.practice_name === PRACTICE_NAME_NON_IRRIGATED
                      ? (overrideFinalNonIrrigated ?? fsaCountyYield)
                      : (overrideFinalNonIrrigated ?? overrideFinalIrrigated ?? fsaCountyYield);

                const priceForActual = overrideMyap ?? marketYearAveragePrice?.price ?? 0;
                const yieldForActual = finalCountyYield ?? fsaCountyYield;

                const arcCoRequest = {
                  crop_year: currentBaseAcres?.crop_year,
                  state_name: currentBaseAcres?.state_name || currentBaseAcres?.location_state_name,
                  county_name: currentBaseAcres?.county_name || currentBaseAcres?.location_county_name,
                  commodity_name: currentBaseAcres?.commodity_name,
                  base_acreage: currentBaseAcres?.base_acreage,
                  share_percent: currentBaseAcres?.share_percent,
                  actual_revenue: priceForActual * yieldForActual,
                  benchmark_price: currentArcCoYield?.benchmark_price,
                  benchmark_yield: currentArcCoYield?.benchmark_yield,
                  apiHelper: ApiHelper,
                };

                const arccoPaymentResponse = calculateArcCoPayment(arcCoRequest);
                const hip = getDefaultHistoricalIrrigationPercentage(
                  currentBaseAcres?.historical_irrigation_percentage
                );

                if (currentArcCoYield.practice_name === PRACTICE_NAME_IRRIGATED) {
                  arcPaymentTotal += (arccoPaymentResponse?.arcPayment || 0) * hip;
                } else if (currentArcCoYield.practice_name === PRACTICE_NAME_NON_IRRIGATED) {
                  arcPaymentTotal += (arccoPaymentResponse?.arcPayment || 0) * (1 - hip);
                } else {
                  arcPaymentTotal += arccoPaymentResponse?.arcPayment || 0;
                }
              });

              totalArcPayment += arcPaymentTotal;
            }

            // Calculate PLC payment if program election is PLC
            if (programElection.program_election === PROGRAM_NAME_PLC) {
              const plcMyap = overrideMyap ?? marketYearAveragePrice?.price;
              const plcRequest = {
                crop_year: currentBaseAcres?.crop_year,
                state_name: currentBaseAcres?.state_name || currentBaseAcres?.location_state_name,
                county_name: currentBaseAcres?.county_name || currentBaseAcres?.location_county_name,
                commodity_name: currentBaseAcres?.commodity_name,
                base_acreage: currentBaseAcres?.base_acreage,
                plc_yield: currentBaseAcres?.plc_yield,
                share_percent: currentBaseAcres?.share_percent,
                effective_reference_price: effectiveReferencePrice?.effective_reference_price,
                effective_price: plcMyap,
                market_year_average_price: plcMyap,
                national_loan_rate: plcPaymentRate?.national_loan_rate || 0,
                apiHelper: ApiHelper,
              };

              const plcPaymentResponse = calculatePlcPayment(plcRequest);
              totalPlcPayment += plcPaymentResponse?.plcPayment || 0;
            }
          }

          return { commodityName, payment: totalArcPayment + totalPlcPayment };
        });

        const paymentResults = await Promise.all(paymentPromises);
        const paymentsMap = {};
        paymentResults.forEach(({ commodityName, payment }) => {
          paymentsMap[commodityName?.toLowerCase()] = payment;
        });

        return paymentsMap;
      } catch (err) {
        console.warn("Error calculating ARC/PLC payments by election:", err);
        return {};
      }
    },
    [
      baseAcres,
      programElections,
      plcPaymentRates,
      marketYearAveragePrices,
      effectiveReferencePrices,
      arccoYields,
      arcPlcActions,
      arcOverridesStableKey,
      countyYieldOverrideStableKey,
      final_county_yield_irrigated,
      final_county_yield_non_irrigated,
      market_year_average_price,
    ]
  );

  const selectArcPlc = async (currentBaseAcres) => {
    if (
      currentBaseAcres?.crop_year &&
      currentBaseAcres?.commodity_name &&
      currentBaseAcres?.base_acreage &&
      currentBaseAcres?.plc_yield &&
      (currentBaseAcres?.state_name || currentBaseAcres?.location_state_name) &&
      (currentBaseAcres?.county_name || currentBaseAcres?.location_county_name)
    ) {
      try {
        const _currentTypeName = currentBaseAcres?.type_name || null;

        const _currentPlcPaymentRates = plcPaymentRates?.find(
          (plc) =>
            plc?.commodity_name?.toLowerCase() === currentBaseAcres?.commodity_name?.toLowerCase() &&
            plc?.crop_year == currentBaseAcres?.crop_year &&
            (_currentTypeName ? plc?.type_name?.toLowerCase() === _currentTypeName.toLowerCase() : true)
        );
        let _currentPlcPaymentRate = {};
        if (_currentPlcPaymentRates?.length) {
          _currentPlcPaymentRate = _currentPlcPaymentRates[0];
        }

        const _currentMarketYearAveragePrices = marketYearAveragePrices?.find(
          (row) =>
            row?.commodity_name?.toLowerCase() === currentBaseAcres?.commodity_name?.toLowerCase() &&
            row?.crop_year == currentBaseAcres?.crop_year &&
            (_currentTypeName ? row?.type_name?.toLowerCase() === _currentTypeName.toLowerCase() : true)
        )?.MarketYearAveragePrices;
        let _currentMarketYearAveragePrice = {};
        if (_currentMarketYearAveragePrices?.length) {
          _currentMarketYearAveragePrice = _currentMarketYearAveragePrices[0];
        }

        const _currentEffectiveReferencePrices = effectiveReferencePrices?.find(
          (row) =>
            row?.commodity_name?.toLowerCase() === currentBaseAcres?.commodity_name?.toLowerCase() &&
            row?.crop_year == currentBaseAcres?.crop_year &&
            (_currentTypeName ? row?.type_name?.toLowerCase() === _currentTypeName.toLowerCase() : true)
        );
        let _currentEffectiveReferencePrice = {};
        if (_currentEffectiveReferencePrices?.EffectiveReferencePrices?.length) {
          _currentEffectiveReferencePrice = _currentEffectiveReferencePrices?.EffectiveReferencePrices[0];
        }

        const _countyArcCoYields = arccoYields?.filter(
          (_arcCoYield) =>
            _arcCoYield?.state_name?.toLowerCase() ===
              (currentBaseAcres?.state_name || currentBaseAcres?.location_state_name)?.toLowerCase() &&
            _arcCoYield?.county_name?.toLowerCase() ===
              (currentBaseAcres?.county_name || currentBaseAcres?.location_county_name)?.toLowerCase() &&
            _arcCoYield?.commodity_name?.toLowerCase() === currentBaseAcres?.commodity_name?.toLowerCase() &&
            _arcCoYield?.crop_year == currentBaseAcres?.crop_year &&
            (_currentTypeName ? _arcCoYield?.type_name?.toLowerCase() === _currentTypeName.toLowerCase() : true)
        );

        const rowOverride = findArcCoCountyYieldOverrideForBaseAcre(currentBaseAcres, arcCoCountyYieldOverrides);
        const overrideMyap = resolveArcOverrideMyap(
          rowOverride,
          market_year_average_price,
          countyYieldOverrideRecord
        );
        const overrideFinalIrrigated = resolveArcOverrideYield(
          rowOverride,
          "final_county_yield_irrigated",
          final_county_yield_irrigated,
          countyYieldOverrideRecord
        );
        const overrideFinalNonIrrigated = resolveArcOverrideYield(
          rowOverride,
          "final_county_yield_non_irrigated",
          final_county_yield_non_irrigated,
          countyYieldOverrideRecord
        );

        let _arccoResponse = null;
        let _arrcoIrrigatedResponse = null;
        let _arccoNonIrrigatedResponse = null;

        _countyArcCoYields?.forEach((_currentArcCoYield) => {
          const fsaCountyYield = fsaArcCoCountyYield(_currentArcCoYield);
          const finalCountyYield =
            _currentArcCoYield.practice_name === PRACTICE_NAME_IRRIGATED
              ? (overrideFinalIrrigated ?? fsaCountyYield)
              : _currentArcCoYield.practice_name === PRACTICE_NAME_NON_IRRIGATED
                ? (overrideFinalNonIrrigated ?? fsaCountyYield)
                : (overrideFinalNonIrrigated ?? overrideFinalIrrigated ?? fsaCountyYield);

          _currentArcCoYield.final_county_yield = finalCountyYield;

          const priceForActual = overrideMyap ?? _currentMarketYearAveragePrice?.price ?? 0;
          const yieldForActual = finalCountyYield ?? fsaCountyYield;

          const _arcCoRequest = {
            crop_year: currentBaseAcres?.crop_year,
            state_name: currentBaseAcres?.state_name || currentBaseAcres?.location_state_name,
            county_name: currentBaseAcres?.county_name || currentBaseAcres?.location_county_name,
            commodity_name: currentBaseAcres?.commodity_name,

            base_acreage: currentBaseAcres?.base_acreage,

            share_percent: currentBaseAcres?.share_percent,

            actual_revenue: priceForActual * yieldForActual,

            // ARCCoPrices
            benchmark_price: _currentArcCoYield?.benchmark_price,
            benchmark_yield: _currentArcCoYield?.benchmark_yield,

            apiHelper: ApiHelper,
          };

          const _arccoPaymentResponse = {
            ...calculateArcCoPayment(_arcCoRequest),
            ..._arcCoRequest,
          };

          // Replace null (but not 0) with default
          const HIP = getDefaultHistoricalIrrigationPercentage(currentBaseAcres.historical_irrigation_percentage);

          if (_currentArcCoYield.practice_name === PRACTICE_NAME_IRRIGATED) {
            _arrcoIrrigatedResponse = _arccoPaymentResponse;
            _arrcoIrrigatedResponse.practice_name = PRACTICE_NAME_IRRIGATED;
            _arrcoIrrigatedResponse.benchmark_yield = _currentArcCoYield?.benchmark_yield;
            _arrcoIrrigatedResponse.historical_irrigation_percentage = HIP;
          } else if (_currentArcCoYield.practice_name === PRACTICE_NAME_NON_IRRIGATED) {
            _arccoNonIrrigatedResponse = _arccoPaymentResponse;
            _arccoNonIrrigatedResponse.practice_name = PRACTICE_NAME_NON_IRRIGATED;
            _arccoNonIrrigatedResponse.benchmark_yield = _currentArcCoYield?.benchmark_yield;
            _arccoNonIrrigatedResponse.historical_irrigation_percentage = HIP;
          } else {
            _arccoResponse = _arccoPaymentResponse;
            _arccoResponse.benchmark_yield = _currentArcCoYield?.benchmark_yield;
            _arccoResponse.historical_irrigation_percentage = 0.0;
          }
        }); //foreach(countyYield - IRR/NI or NTS)

        if (_countyArcCoYields?.length > 1) {
          // Combine responses:
          _arccoResponse = {};

          _arccoResponse.benchmark_yield_irrigated = _arrcoIrrigatedResponse?.benchmark_yield;
          _arccoResponse.benchmark_yield_non_irrigated = _arccoNonIrrigatedResponse?.benchmark_yield;

          _arccoResponse.arcPayment =
            _arrcoIrrigatedResponse?.arcPayment * _arrcoIrrigatedResponse?.historical_irrigation_percentage +
            _arccoNonIrrigatedResponse?.arcPayment * (1 - _arrcoIrrigatedResponse?.historical_irrigation_percentage);

          _arccoResponse.totalFarmPayment =
            _arrcoIrrigatedResponse?.totalFarmPayment * _arrcoIrrigatedResponse?.historical_irrigation_percentage +
            _arccoNonIrrigatedResponse?.totalFarmPayment *
              (1 - _arrcoIrrigatedResponse?.historical_irrigation_percentage);

          _arccoResponse.benchmark_guarantee =
            _arrcoIrrigatedResponse?.benchmark_guarantee * _arrcoIrrigatedResponse?.historical_irrigation_percentage +
            _arccoNonIrrigatedResponse?.benchmark_guarantee *
              (1 - _arrcoIrrigatedResponse?.historical_irrigation_percentage);

          _arccoResponse.benchmark_revenue =
            _arrcoIrrigatedResponse?.benchmark_revenue * _arrcoIrrigatedResponse?.historical_irrigation_percentage +
            _arccoNonIrrigatedResponse?.benchmark_revenue *
              (1 - _arrcoIrrigatedResponse?.historical_irrigation_percentage);

          _arccoResponse.actual_revenue =
            _arrcoIrrigatedResponse?.actual_revenue * _arrcoIrrigatedResponse?.historical_irrigation_percentage +
            _arccoNonIrrigatedResponse?.actual_revenue *
              (1 - _arrcoIrrigatedResponse?.historical_irrigation_percentage);
        }

        const _plcRequest = {
          crop_year: currentBaseAcres?.crop_year,
          state_name: currentBaseAcres?.state_name || currentBaseAcres?.location_state_name,
          county_name: currentBaseAcres?.county_name || currentBaseAcres?.location_county_name,
          commodity_name: currentBaseAcres?.commodity_name,

          base_acreage: currentBaseAcres?.base_acreage,
          plc_yield: currentBaseAcres?.plc_yield,

          share_percent: currentBaseAcres?.share_percent,

          // PLCPaymentRates
          effective_reference_price: _currentEffectiveReferencePrice?.effective_reference_price,
          effective_price: overrideMyap ?? _currentMarketYearAveragePrice?.price,
          market_year_average_price: overrideMyap ?? _currentMarketYearAveragePrice?.price,
          national_loan_rate: _currentPlcPaymentRate?.national_loan_rate || 0,

          apiHelper: ApiHelper,
        };
        const _plcPayment = calculatePlcPayment(_plcRequest);

        const arcPlcResponse = {
          arcPayment: _arccoResponse?.arcPayment,
          arcPaymentIrrigated: _arrcoIrrigatedResponse?.arcPayment,
          arcPaymentNonIrrigated: _arccoNonIrrigatedResponse?.arcPayment,

          benchmark_yield_irrigated: _arccoResponse?.benchmark_yield_irrigated,
          benchmark_yield_non_irrigated: _arccoResponse?.benchmark_yield_non_irrigated,

          arccoResponsesByPractice: [_arrcoIrrigatedResponse || _arccoResponse, _arccoNonIrrigatedResponse].filter(
            (r) => r
          ),

          benchmark_guarantee: _arccoResponse?.benchmark_guarantee,

          plcPayment: _plcPayment.plcPayment,
          plcCalculation: {
            inputs: {
              effective_reference_price: _plcRequest.effective_reference_price,
              effective_price: _plcRequest.effective_price,
              national_loan_rate: _plcRequest.national_loan_rate,
              market_year_average_price: _plcRequest.market_year_average_price,
              plc_yield: _plcRequest.plc_yield,
              base_acreage: _plcRequest.base_acreage,
              share_percent: _plcRequest.share_percent,
            },
            outputs: _plcPayment,
            constants: {
              PLC_PAYMENT_PERCENTAGE: PLC_PAYMENT_PERCENTAGE,
            },
          },
        };

        if (arcPlcResponse) {
          if (arcPlcResponse?.arcPayment?.error) {
            setIsErrorArcPlc(true);
            enqueueSnackbar("Could not load ARC-CO projections.", { preventDuplicate: true, variant: "warning" });
            console.warn(arcPlcResponse);
          }
          if (arcPlcResponse?.plcPayment?.error) {
            setIsErrorArcPlc(true);
            enqueueSnackbar("Could not load PLC projections.", { preventDuplicate: true, variant: "warning" });
            console.warn(arcPlcResponse);
          }

          return arcPlcResponse;
        }
      } catch (err) {
        console.warn(err);
        enqueueSnackbar("Could not load PLC projections.", { preventDuplicate: true, variant: "warning" });
      }
    }
  };

  // --- --- ---

  const [arcPlc, setArcPlc] = useState({});
  const [isLoadingArcPlc, setIsLoadingArcPlc] = useState(true);
  const [isErrorArcPlc, setIsErrorArcPlc] = useState(false);

  const [totals, setTotals] = useState({});
  const [crops, setCrops] = useState([]);

  // Stress Test States
  const [arcCoStressTestResults, setArcCoStressTestResults] = useState(null);
  const [arcCoStressTestResultsIrrigated, setArcCoStressTestResultsIrrigated] = useState(null);
  const [arcCoStressTestResultsNonIrrigated, setArcCoStressTestResultsNonIrrigated] = useState(null);
  const [plcStressTestResults, setPlcStressTestResults] = useState(null);

  const loadArcPlc = async () => {
    try {
      setIsLoadingArcPlc(true);

      const _arcPlcResponses_async = [];
      for (var currentBaseAcre of baseAcres) {
        const _arcPlc = await selectArcPlc(currentBaseAcre);
        _arcPlcResponses_async.push({
          baseAcres: currentBaseAcre,
          arcPlcResponse: _arcPlc,
        });
      }

      const _arcPlcResponses = (await Promise.all(_arcPlcResponses_async)).filter((_ap) => _ap);
      setArcPlc(_arcPlcResponses);

      const _totals = {
        arcPayment: 0,
        plcPayment: 0,
        base_acreage: 0,
        plc_yield: 0,
        benchmark_guarantee: 0,
        share_percent: 0,
      };

      let plcCalcsForTotal = {};

      _arcPlcResponses.forEach((_currentArcPlc) => {
        if (_currentArcPlc?.arcPlcResponse?.arcPayment) _totals.arcPayment += _currentArcPlc.arcPlcResponse.arcPayment;
        if (_currentArcPlc?.arcPlcResponse?.plcPayment) _totals.plcPayment += _currentArcPlc.arcPlcResponse.plcPayment;
        if (_currentArcPlc?.baseAcres) _totals.base_acreage += _currentArcPlc.baseAcres.base_acreage;
        if (_currentArcPlc?.baseAcres) {
          _totals.plc_yield += _currentArcPlc.baseAcres.plc_yield * _currentArcPlc.baseAcres.base_acreage;
          _totals.share_percent +=
            (_currentArcPlc.baseAcres.share_percent || 1) * _currentArcPlc.baseAcres.base_acreage;
        }
        if (_currentArcPlc?.arcPlcResponse?.benchmark_guarantee)
          _totals.benchmark_guarantee += _currentArcPlc?.arcPlcResponse?.benchmark_guarantee;
        if (_currentArcPlc?.arcPlcResponse?.plcCalculation && !plcCalcsForTotal.inputs) {
          plcCalcsForTotal = _currentArcPlc?.arcPlcResponse?.plcCalculation;
        }

        // IRR + NI
        _totals.benchmark_yield_irrigated = _currentArcPlc.arcPlcResponse?.benchmark_yield_irrigated;
        _totals.benchmark_yield_non_irrigated = _currentArcPlc.arcPlcResponse?.benchmark_yield_non_irrigated;
        _totals.arccoResponsesByPractice = _currentArcPlc.arcPlcResponse?.arccoResponsesByPractice;
      });
      _totals.plc_yield = _totals.plc_yield / _.sumBy(_arcPlcResponses, (arcplc) => arcplc.baseAcres.base_acreage); //weighted avg.
      _totals.share_percent =
        _totals.share_percent / _.sumBy(_arcPlcResponses, (arcplc) => arcplc.baseAcres.base_acreage); //weighted avg.

      if (_arcPlcResponses.length > 0) {
        const totalPlcRequest = {
          ...plcCalcsForTotal.inputs,
          base_acreage: _totals.base_acreage,
          plc_yield: _totals.plc_yield,
          share_percent: _totals.share_percent,
        };
        const totalPlcPayment = calculatePlcPayment(totalPlcRequest);
        _totals.plcCalculation = {
          inputs: totalPlcRequest,
          outputs: totalPlcPayment,
          constants: plcCalcsForTotal.constants,
        };
      }

      // Flatten
      const allResponsesByPractice = _arcPlcResponses.flatMap(
        (_currentArcPlc) => _currentArcPlc.arcPlcResponse?.arccoResponsesByPractice
      );

      const allIrrigatedResponsesByPractice = allResponsesByPractice.filter(
        (r) => r?.practice_name === PRACTICE_NAME_IRRIGATED
      );
      const allNonIrrigatedResponsesByPractice = allResponsesByPractice.filter(
        (r) => r?.practice_name === PRACTICE_NAME_NON_IRRIGATED
      );

      let totalsByPracticeIrrigated = null;
      if (allIrrigatedResponsesByPractice?.length) {
        totalsByPracticeIrrigated = _totals.arccoResponsesByPractice.find(
          (r) => r?.practice_name === PRACTICE_NAME_IRRIGATED
        );
        if (totalsByPracticeIrrigated) {
          totalsByPracticeIrrigated.arcPayment = _.sumBy(allIrrigatedResponsesByPractice, (x) => x?.arcPayment);
          totalsByPracticeIrrigated.payment = _.sumBy(allIrrigatedResponsesByPractice, (x) => x?.totalFarmPayment);
          totalsByPracticeIrrigated.totalFarmPayment = _.sumBy(
            allIrrigatedResponsesByPractice,
            (x) => x?.totalFarmPayment
          );
          totalsByPracticeIrrigated.maxPayment = _.sumBy(allIrrigatedResponsesByPractice, (x) => x?.maxPayment);
          totalsByPracticeIrrigated.base_acreage = _.sumBy(allIrrigatedResponsesByPractice, (x) => x?.base_acreage);
        }
      }
      let totalsByPracticeNonIrrigated = null;
      if (allNonIrrigatedResponsesByPractice?.length) {
        totalsByPracticeNonIrrigated = _totals.arccoResponsesByPractice.find(
          (r) => r?.practice_name === PRACTICE_NAME_NON_IRRIGATED
        );
        if (totalsByPracticeNonIrrigated) {
          totalsByPracticeNonIrrigated.arcPayment = _.sumBy(allNonIrrigatedResponsesByPractice, (x) => x?.arcPayment);
          totalsByPracticeNonIrrigated.payment = _.sumBy(
            allNonIrrigatedResponsesByPractice,
            (x) => x?.totalFarmPayment
          );
          totalsByPracticeNonIrrigated.totalFarmPayment = _.sumBy(
            allNonIrrigatedResponsesByPractice,
            (x) => x?.totalFarmPayment
          );
          totalsByPracticeNonIrrigated.maxPayment = _.sumBy(allNonIrrigatedResponsesByPractice, (x) => x?.maxPayment);
          totalsByPracticeNonIrrigated.base_acreage = _.sumBy(
            allNonIrrigatedResponsesByPractice,
            (x) => x?.base_acreage
          );
        }
      }

      if (allIrrigatedResponsesByPractice?.length || allNonIrrigatedResponsesByPractice?.length) {
        _totals.arccoResponsesByPractice = [totalsByPracticeIrrigated, totalsByPracticeNonIrrigated]?.filter((x) => x);
      } else if (_totals.arccoResponsesByPractice?.[0]) {
        // Combine totals: sum up base acres on the _totals.arccoResponsesByPractice to be equal to ALL base_acreage from all ARC/PLC responses
        _totals.arccoResponsesByPractice[0].base_acreage = _.sumBy(allResponsesByPractice, (x) => x?.base_acreage);
        _totals.arccoResponsesByPractice[0].payment = _.sumBy(allResponsesByPractice, (x) => x?.payment);
        _totals.arccoResponsesByPractice[0].totalFarmPayment = _.sumBy(
          allResponsesByPractice,
          (x) => x?.totalFarmPayment
        );
        _totals.arccoResponsesByPractice[0].maxPayment = _.sumBy(allResponsesByPractice, (x) => x?.maxPayment);
        _totals.arccoResponsesByPractice[0].arcPayment = _.sumBy(allResponsesByPractice, (x) => x?.arcPayment);
      }

      // Share-weighted HIP across FSNs (not last Search row). Same pattern as share_percent below.
      const countyHip = getShareWeightedHistoricalIrrigationPercentage(baseAcres);

      // Worksheet Step 5 reads practice.share_percent. Stamp acre-weighted share (already on
      // _totals) so mixed 60%/100% farms show ~78%, not the last farm's 100% (which hid Step 5).
      _totals.arccoResponsesByPractice = (_totals.arccoResponsesByPractice || [])
        .filter(Boolean)
        .map((r) => ({
          ...r,
          share_percent: _totals.share_percent,
          historical_irrigation_percentage: countyHip,
        }));

      setTotals(_totals);

      const _cropKeys = _.uniqBy(
        _arcPlcResponses.map((_arcplc) => ({
          commodity_name: _arcplc?.baseAcres?.commodity_name,
          type_name: _arcplc?.baseAcres?.type_name || null,
        })),
        (k) => `${k.commodity_name}||${k.type_name}`
      );
      const _crops = _cropKeys.map((key) => ({ ...key }));
      for (const _currentCrop of _crops) {
        const _cropArcPlcResponses = _arcPlcResponses.filter(
          (_arcPlc) =>
            _arcPlc?.baseAcres?.commodity_name === _currentCrop?.commodity_name &&
            (_arcPlc?.baseAcres?.type_name || null) === _currentCrop?.type_name
        );
        const _cropTotals = {
          arcPayment: 0,
          plcPayment: 0,
          base_acreage: 0,
          plc_yield: 0,
        };
        _cropArcPlcResponses.forEach((_currentArcPlc) => {
          if (_currentArcPlc?.arcPlcResponse?.arcPayment)
            _cropTotals.arcPayment += _currentArcPlc.arcPlcResponse.arcPayment;
          if (_currentArcPlc?.arcPlcResponse?.plcPayment)
            _cropTotals.plcPayment += _currentArcPlc.arcPlcResponse.plcPayment;
          if (_currentArcPlc?.baseAcres) _cropTotals.base_acreage += _currentArcPlc.baseAcres.base_acreage;
          if (_currentArcPlc?.baseAcres)
            _cropTotals.plc_yield += _currentArcPlc.baseAcres.plc_yield * _currentArcPlc.baseAcres.base_acreage;
        });
        _cropTotals.plc_yield =
          _cropTotals.plc_yield / _.sumBy(_cropArcPlcResponses, (arcplc) => arcplc.baseAcres.base_acreage);

        _currentCrop.arcPayment = _cropTotals.arcPayment;
        _currentCrop.plcPayment = _cropTotals.plcPayment;
        _currentCrop.base_acreage = _cropTotals.base_acreage;
        _currentCrop.plc_yield = _cropTotals.plc_yield;
      }
      setCrops(_crops);

      setIsLoadingArcPlc(false);

      return _totals;
    } catch (err) {
      setIsLoadingArcPlc(false);
      setIsErrorArcPlc(true);
      setArcPlc({});
      console.warn(err);
      enqueueSnackbar("Could not load ARC/PLC projections.", { preventDuplicate: true, variant: "error" });
    }
  };

  useEffect(() => {
    if (
      baseAcres?.length &&
      arccoYields !== null &&
      marketYearAveragePrices !== null &&
      effectiveReferencePrices !== null
      //  && plcPaymentRates !== null
    ) {
      loadArcPlc();
    } else if (!baseAcres?.length) {
      setIsLoadingArcPlc(false);
    }
  }, [
    baseAcresStableKey,
    arccoYields,
    marketYearAveragePrices,
    effectiveReferencePrices,
    final_county_yield_irrigated,
    final_county_yield_non_irrigated,
    market_year_average_price,
    arcOverridesStableKey,
    countyYieldOverrideStableKey,
  ]);

  // --- Stress Test Functions ---

  const loadArcCoStressTestResults = useCallback(async () => {
    try {
      // Clear previous results to avoid showing stale data while calculating
      setArcCoStressTestResults(null);
      setArcCoStressTestResultsIrrigated(null);
      setArcCoStressTestResultsNonIrrigated(null);

      // Get the current data needed for calculation
      const currentCropYear = crop_year || (baseAcres?.length ? baseAcres[0]?.crop_year : null);
      const currentMarketYearAveragePrices = marketYearAveragePrices?.find(
        (row) =>
          row?.commodity_name?.toLowerCase() === commodity_name?.toLowerCase() && row?.crop_year == currentCropYear
      )?.MarketYearAveragePrices?.[0];

      if (!currentMarketYearAveragePrices) {
        console.warn("No market year average prices found for stress test", commodity_name, currentCropYear);
        return;
      }

      // Get ARC-CO yields data
      const ARCCOYields = arccoYields?.filter(
        (_yield) =>
          _yield?.state_name?.toLowerCase() === location_state_name?.toLowerCase() &&
          _yield?.county_name?.toLowerCase() === location_county_name?.toLowerCase() &&
          _yield?.commodity_name?.toLowerCase() === commodity_name?.toLowerCase() &&
          _yield?.crop_year == currentCropYear
      );

      const ARCCOYieldsDefault = ARCCOYields?.[0];
      const ARCCOYieldsIrrigated = ARCCOYields?.find((y) => y.practice_name === PRACTICE_NAME_IRRIGATED);
      const ARCCOYieldsNonIrrigated = ARCCOYields?.find((y) => y.practice_name === PRACTICE_NAME_NON_IRRIGATED);

      if (!ARCCOYields?.length) {
        console.warn("No ARC-CO yields found for stress test");
        return;
      }

      // Pass budget county practices if available for county data loading
      const { actions: insuranceActions } = new InsuranceService({
        apiConfig: globalState,
        crop_year: currentCropYear,
        budgetCountyPractices: budgetCountyPractices,
      });

      let countyData = null;
      try {
        const countyDataLoadingPractice = budgetCountyPractices?.length
          ? budgetCountyPractices[0]
          : {
              crop_year: currentCropYear,
              location_state_name,
              location_county_name,
              commodity_name,
              ...(type_name && { type_name }),
            };
        countyData = await insuranceActions.loadCountyData(countyDataLoadingPractice);
      } catch (countyDataError) {
        console.error("Could not load county data for stress test:", countyDataError);
      }

      const _reported_acreage = budgetTotalReportedAcreage;
      const baseFarmParameters = budgetCountyPractices?.[0] || {
        crop_year: currentCropYear,
        location_state_name,
        location_county_name,
        commodity_name,
        ...(type_name && { type_name }),
      };

      if (ARCCOYields?.length > 1 && historicalIrrigationPercentage !== 1) {
        // COMBINED IRR+NI Stress tests
        const stressTestParametersIrrigated =
          arcCoStressTestParametersIrrigated ||
          arcPlcActions.createStressTestParameters({
            commodity_name,
            benchmarkYield: fsaArcCoCountyYield(ARCCOYieldsIrrigated),
            marketYearAveragePrices: currentMarketYearAveragePrices,
            plcYield: totals?.plc_yield,
          });

        setStressTestParametersOutput(stressTestParametersIrrigated);

        const stressTestParametersNonIrrigated =
          arcCoStressTestParametersNonIrrigated ||
          arcPlcActions.createStressTestParameters({
            commodity_name,
            benchmarkYield: fsaArcCoCountyYield(ARCCOYieldsNonIrrigated),
            marketYearAveragePrices: currentMarketYearAveragePrices,
            plcYield: totals?.plc_yield,
          });

        const stressTestPropsIrrigated = {
          arcCoYields: ARCCOYieldsIrrigated,
          farmParameters: {
            ...baseFarmParameters,
            reported_acreage: _reported_acreage * historicalIrrigationPercentage,
            base_acreage: totals?.base_acreage * historicalIrrigationPercentage,
          },
          countyParameters: [countyData],
          testParameters: stressTestParametersIrrigated,
        };

        const stressTestPropsNonIrrigated = {
          arcCoYields: ARCCOYieldsNonIrrigated,
          farmParameters: {
            ...baseFarmParameters,
            reported_acreage: _reported_acreage * (1 - historicalIrrigationPercentage),
            base_acreage: totals?.base_acreage * (1 - historicalIrrigationPercentage),
          },
          countyParameters: [countyData],
          testParameters: stressTestParametersNonIrrigated,
        };

        const _arccoStressTestResultsIrrigated = await calculateArcCoStressTest(stressTestPropsIrrigated);
        const _arccoStressTestResultsNonIrrigated = await calculateArcCoStressTest(stressTestPropsNonIrrigated);

        // Create weighted stress test results by combining irrigated and non-irrigated results
        const _arccoStressTestResultsWeighted = createWeightedStressTestResults(
          _arccoStressTestResultsIrrigated,
          _arccoStressTestResultsNonIrrigated
        );

        setArcCoStressTestResults(_arccoStressTestResultsWeighted);
        setArcCoStressTestResultsIrrigated(_arccoStressTestResultsIrrigated);
        setArcCoStressTestResultsNonIrrigated(_arccoStressTestResultsNonIrrigated);
      } else if (ARCCOYields?.length === 1 || historicalIrrigationPercentage === 1) {
        // Single practice stress test
        const stressTestParameters =
          arcCoStressTestParametersIrrigated ||
          arcPlcActions.createStressTestParameters({
            commodity_name,
            benchmarkYield: fsaArcCoCountyYield(ARCCOYieldsDefault),
            marketYearAveragePrices: currentMarketYearAveragePrices,
            plcYield: totals?.plc_yield,
          });

        setStressTestParametersOutput(stressTestParameters);

        const stressTestProps = {
          arcCoYields: ARCCOYieldsDefault,
          farmParameters: {
            ...baseFarmParameters,
            reported_acreage: _reported_acreage,
            base_acreage: totals?.base_acreage,
          },
          countyParameters: [countyData],
          insurancePlanParameters: null,
          testParameters: stressTestParameters,
        };

        const _arccoStressTestResults = await calculateArcCoStressTest(stressTestProps);
        setArcCoStressTestResults(_arccoStressTestResults);
      }
    } catch (error) {
      enqueueSnackbar(error.message || "Error loading ARC-CO stress test results", {
        preventDuplicate: true,
        variant: "error",
      });
    }
  }, [
    baseAcres?.length,
    totals?.base_acreage,
    budgetCountyPractices,
    // Stable API identity (avoid `globalState` reference churn from `{ ...globalState }` callers).
    globalState?.producer_token,
    globalState?.agent_token,
    historicalIrrigationPercentage,
    arcCoDisplayMode,
    marketYearAveragePrices,
    arccoYields,
    budgetTotalReportedAcreage,
    location_state_name,
    location_county_name,
    commodity_name,
    type_name,
    crop_year,
    arcPlcActions,
    enqueueSnackbar,
    arcCoStressTestParametersIrrigated,
    arcCoStressTestParametersNonIrrigated,
  ]);

  const loadPlcStressTestResults = useCallback(async () => {
    try {
      setPlcStressTestResults(null);

      const currentCropYear = crop_year || (baseAcres?.length ? baseAcres[0]?.crop_year : null);
      const currentMarketYearAveragePrices = marketYearAveragePrices?.find(
        (row) =>
          row?.commodity_name?.toLowerCase() === commodity_name?.toLowerCase() && row?.crop_year == currentCropYear
      )?.MarketYearAveragePrices?.[0];

      const currentEffectiveReferencePrices = effectiveReferencePrices?.find(
        (row) =>
          row?.commodity_name?.toLowerCase() === commodity_name?.toLowerCase() && row?.crop_year == currentCropYear
      )?.EffectiveReferencePrices?.[0];

      if (!currentMarketYearAveragePrices || !currentEffectiveReferencePrices) {
        console.warn("Missing required data for PLC stress test");
        return;
      }

      // Create dummy budget county practice for PLC
      const dummyPlcBudgetCountyPractice = arcPlcActions.createDummyPlcBudgetCountyPractice({
        crop_year: currentCropYear,
        location_state_name,
        location_county_name,
        commodity_name,
        ...(type_name && { type_name }),
        marketYearAveragePrices: currentMarketYearAveragePrices,
      });

      const { actions: insuranceActions } = new InsuranceService({
        apiConfig: globalState,
        crop_year: currentCropYear,
      });

      let countyData;
      try {
        countyData = await insuranceActions.loadCountyData(dummyPlcBudgetCountyPractice);
      } catch {
        // If county data loading fails, create minimal county data with required fields
        countyData = {
          ...dummyPlcBudgetCountyPractice,
          projected_price: currentMarketYearAveragePrices?.price || 4.0,
          price_volatility_factor: 0.2,
          expected_index_value: 100,
          reinsurance_year: currentCropYear,
        };
      }

      // Get PLC payment rates for this commodity/year
      const _plcPaymentRatesAll = await arcPlcActions.getPLCPaymentRates();
      const plcPaymentRatesForCommodity = _plcPaymentRatesAll?.find(
        (p) => p.commodity_name.toLowerCase() === commodity_name.toLowerCase()
      );

      const _reported_acreage = budgetTotalReportedAcreage;

      const stressTestParameters =
        plcStressTestParameters ||
        arcPlcActions.createStressTestParameters({
          commodity_name,
          benchmarkYield: totals?.plc_yield,
          marketYearAveragePrices: currentMarketYearAveragePrices,
          plcYield: totals?.plc_yield,
        });

      const plcBaseFarmParameters = budgetCountyPractices?.[0] || dummyPlcBudgetCountyPractice;

      const stressTestProps = {
        plcPaymentRates: plcPaymentRatesForCommodity,
        farmParameters: {
          ...plcBaseFarmParameters,
          reported_acreage: _reported_acreage,
          base_acreage: totals?.base_acreage,
          plc_yield: totals?.plc_yield,
          effective_reference_price: currentEffectiveReferencePrices?.effective_reference_price,
        },
        countyParameters: [countyData],
        insurancePlanParameters: null,
        testParameters: stressTestParameters,
      };

      const _plcStressTestResults = await calculatePlcStressTest(stressTestProps);
      setPlcStressTestResults(_plcStressTestResults);
    } catch (error) {
      enqueueSnackbar(error.message || "Error loading PLC stress test results", {
        preventDuplicate: true,
        variant: "error",
      });
    }
  }, [
    baseAcres?.length,
    totals?.base_acreage,
    totals?.plc_yield,
    budgetCountyPractices,
    effectiveReferencePrices,
    globalState?.producer_token,
    globalState?.agent_token,
    arcCoDisplayMode,
    marketYearAveragePrices,
    budgetTotalReportedAcreage,
    location_state_name,
    location_county_name,
    commodity_name,
    type_name,
    crop_year,
    arcPlcActions,
    enqueueSnackbar,
    arcCoStressTestParametersIrrigated,
    arcCoStressTestParametersNonIrrigated,
    plcStressTestParameters,
  ]);

  // Trigger ARC-CO stress test when conditions are met
  useEffect(() => {
    const shouldRunArcCoStressTest =
      baseAcres?.length &&
      ((arcCoDisplayMode === "planted_acre" && budgetTotalReportedAcreage > 0) || arcCoDisplayMode === "base_acre") &&
      marketYearAveragePrices?.length &&
      globalState?.producer_token &&
      totals?.base_acreage &&
      historicalIrrigationPercentage !== null &&
      arccoYields?.length &&
      // Wait for stress test parameters to be loaded (not null)
      (arcCoStressTestParametersIrrigated !== null || arcCoDisplayMode === "base_acre");

    if (shouldRunArcCoStressTest) {
      loadArcCoStressTestResults();
    }
  }, [
    baseAcres?.length,
    totals?.base_acreage,
    budgetCountyPractices,
    globalState?.producer_token,
    historicalIrrigationPercentage,
    arcCoDisplayMode,
    marketYearAveragePrices,
    arccoYields,
    arcCoStressTestParametersIrrigated,
    loadArcCoStressTestResults,
  ]);

  // Trigger PLC stress test when conditions are met
  useEffect(() => {
    const shouldRunPlcStressTest =
      baseAcres?.length &&
      ((arcCoDisplayMode === "planted_acre" && budgetTotalReportedAcreage !== 0) ||
        (arcCoDisplayMode === "base_acre" && totals?.base_acreage > 0)) &&
      effectiveReferencePrices?.length &&
      globalState?.producer_token &&
      totals?.base_acreage &&
      Boolean(marketYearAveragePrices);

    if (shouldRunPlcStressTest) {
      loadPlcStressTestResults();
    }
  }, [
    baseAcres?.length,
    totals?.base_acreage,
    budgetCountyPractices,
    effectiveReferencePrices,
    globalState?.producer_token,
    arcCoDisplayMode,
    marketYearAveragePrices,
    budgetTotalReportedAcreage,
    plcStressTestParameters,
    loadPlcStressTestResults,
  ]);

  // --- --- ---
  // Stable reference so consumers can safely list `actions` in effect deps (object literal would change every render).
  const actions = useMemo(
    () => ({
      getArcPlcByElection,
      loadArcCoStressTestResults,
      loadPlcStressTestResults,
    }),
    [getArcPlcByElection, loadArcCoStressTestResults, loadPlcStressTestResults]
  );

  return {
    responses: arcPlc,
    crops: crops,

    isLoading: isLoadingArcPlc,
    isError: isErrorArcPlc,

    totals: totals,

    // Program Elections
    programElections: programElections,
    isLoadingProgramElections: isLoadingProgramElections,

    // Stress Test Results
    stressTestResults: {
      arcCo: arcCoStressTestResults,
      arcCoIrrigated: arcCoStressTestResultsIrrigated,
      arcCoNonIrrigated: arcCoStressTestResultsNonIrrigated,
      plc: plcStressTestResults,
    },

    stressTestParameters: stressTestParametersOutput,

    // Benchmark Yields for Irrigated practice
    benchmark_yield_irrigated: benchmarkYieldIrrigated,
    // Benchmark Yields for Non-irrigated practice or commodities without irrigation
    benchmark_yield_non_irrigated: benchmarkYieldNonIrrigated,

    actions,
  };
};
